// =============================================================================
// Servicio: InvoiceService
// Descripción: Capa lógica centralizada para el módulo de facturación
//              electrónica AFIP en KronoFlow. Gestiona la emisión de
//              comprobantes vía Edge Function, mantiene el estado reactivo
//              con Signals y escucha actualizaciones en tiempo real
//              a través de Supabase Realtime (multi-tenant por business_id).
//
// Estándares Angular 21 Zoneless:
//   - inject() exclusivamente. Sin constructor de inyección.
//   - Signals para estado reactivo.
//   - UiService para todas las notificaciones visuales (prohibido MatSnackBar directo).
//   - Sin localStorage/sessionStorage directo (usar StorageService si se persiste).
// =============================================================================

import { Injectable, inject, signal } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { RealtimeChannel } from '@supabase/supabase-js';

import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';
import { UiService } from './ui.service';
import { BranchService } from './branch.service';
import {
  Invoice,
  CreateInvoicePayload,
  EstadoFactura,
  InvoiceCambioEstado,
  InvoiceCambioVenta,
  InvoiceSummary,
  InvoiceTemplate,
} from '../models/invoice.model';
import { PaginatedResponse } from '../models/pagination.model';
import { avisoAplicaASucursal, avisoPorCambioEstado } from '../utils/invoice-realtime.util';
import { advertenciasDeRespuesta, mensajeErrorEdgeFunction } from '../utils/edge-function-error.util';
import { CreditNoteApplyContext, CreditNoteContext, SaleRefundLine } from '../models/sales.model';

// ---------------------------------------------------------------------------
// CONSTANTES INTERNAS
// ---------------------------------------------------------------------------

/** Nombre de la Edge Function que procesa la creación de comprobantes */
const EDGE_FUNCTION_INVOICES = 'invoices';

/** Número máximo de comprobantes devueltos en consultas de listado sin paginación */
const LIMITE_LISTADO_DEFAULT = 50;

/** Columnas del listado de comprobantes (vista `invoice_summaries_with_ref`) */
const COLUMNAS_RESUMEN =
  'id, tipo_comprobante, estado, denominacion_receptor, monto_total, fecha_emision, cae, comprobante_numero, punto_venta, created_at, parent_tipo_comprobante, parent_comprobante_numero, parent_punto_venta, ya_tiene_nc';

// ---------------------------------------------------------------------------
// SERVICIO
// ---------------------------------------------------------------------------

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  // -------------------------------------------------------------------------
  // SECCIÓN 1: INYECCIONES (sin constructor, patrón Angular 21)
  // -------------------------------------------------------------------------

  private readonly supabase = inject(SupabaseService).client;
  private readonly auth    = inject(AuthService);
  private readonly ui      = inject(UiService);
  private readonly branches = inject(BranchService);

  // -------------------------------------------------------------------------
  // SECCIÓN 2: ESTADO REACTIVO (Signals)
  // -------------------------------------------------------------------------

  /**
   * Último CAMBIO DE ESTADO real de un comprobante (PENDING → APPROVED, etc.), avisado por la base
   * vía broadcast. No se dispara con otros UPDATE.
   * Las pantallas reaccionan con `effect()` para actualizar solo la fila afectada.
   */
  private readonly _cambioEstado = signal<InvoiceCambioEstado | null>(null);
  readonly cambioEstado = this._cambioEstado.asReadonly();

  /** Último cambio de `sale_effect_status` (anulación de venta por NC aplicada/pendiente/fallida). */
  private readonly _cambioVenta = signal<InvoiceCambioVenta | null>(null);
  readonly cambioVenta = this._cambioVenta.asReadonly();

  /**
   * Indica si hay una operación de emisión en curso.
   * Útil para deshabilitar botones durante el proceso de envío.
   */
  private readonly _emitiendo = signal<boolean>(false);

  /** Signal público de solo lectura del estado de emisión en curso */
  readonly emitiendo = this._emitiendo.asReadonly();

  // -------------------------------------------------------------------------
  // REFERENCIA AL CANAL REALTIME (para limpieza posterior)
  // -------------------------------------------------------------------------

  /** Canal de Supabase Realtime activo para escucha de actualizaciones de facturas */
  private canalRealtime: RealtimeChannel | null = null;

  // -------------------------------------------------------------------------
  // SECCIÓN 3: MÉTODOS PÚBLICOS — Acciones principales
  // -------------------------------------------------------------------------

  /**
   * Emite un comprobante electrónico a través del Gateway AFIP de forma asíncrona.
   *
   * El flujo es:
   * 1. Envía el payload a la Edge Function `invoices` (POST).
   * 2. La Edge Function inserta en BD y reenvía al Gateway AFIP.
   * 3. El Gateway responde 201 inmediatamente con `estado: 'PENDING'`.
   * 4. El resultado final (CAE / rechazo) llega vía Realtime cuando el
   *    webhook de AFIP actualiza el comprobante en `public.invoices`.
   *
   * IMPORTANTE: No esperar el CAE en la respuesta de este método.
   * Suscribirse al signal `cambioEstado` o al evento Realtime
   * para conocer el resultado definitivo.
   *
   * @param payload Datos del comprobante a emitir.
   * @returns Observable con el registro `Invoice` en estado `PENDING`.
   */
  emitirFactura(payload: CreateInvoicePayload): Observable<Invoice> {
    this._emitiendo.set(true);

    return from(
      this.supabase.functions.invoke<Invoice>(EDGE_FUNCTION_INVOICES, {
        body: payload,
      })
    ).pipe(
      switchMap(async ({ data, error }) => {
        if (error) {
          // El motivo real (p. ej. "la sucursal no tiene punto de venta") viene en el body.
          throw new Error(await mensajeErrorEdgeFunction(error, 'Error al comunicarse con la Edge Function de facturas.'));
        }
        if (!data) {
          throw new Error('La Edge Function no devolvió datos del comprobante.');
        }
        // El Gateway acepta la emisión pero puede advertir que la combinación emisor/clase/condición
        // del receptor es dudosa: el comprobante sigue en curso y se avisa al usuario.
        const advertencias = advertenciasDeRespuesta(data);
        if (advertencias.length > 0) {
          this.ui.showWarning(`ARCA advierte: ${advertencias.join(' ')}`, 10000);
        }
        return data;
      }),
      catchError((err: unknown) => {
        const mensaje = err instanceof Error ? err.message : 'Error inesperado al emitir la factura.';
        // Notificamos el error en la UI inmediatamente (el resultado asíncrono
        // de AFIP se notifica por separado vía Realtime)
        this.ui.showError(`Error al iniciar la emisión: ${mensaje}`);
        return throwError(() => new Error(mensaje));
      }),
      // Limpiamos el flag de "emitiendo" en ambos casos (éxito o error)
      // usando finalize-like via tap en el map y el catchError
    );
  }

  /**
   * Obtiene el historial paginado de comprobantes del negocio activo.
   * Filtra en la base por `business_id` (aislamiento multi-tenant) y, si viene, por `branch_id`:
   * una sucursal solo ve sus comprobantes (sin sucursal = modo global, igual que Ventas).
   * Los registros se ordenan por fecha de emisión descendente.
   *
   * @param params Parámetros de paginación, filtros opcionales de estado y búsqueda.
   * @returns Promesa con lista paginada de comprobantes.
   */
  async obtenerFacturasPaginadas(params: {
    businessId: string;
    branchId?: string | null;
    estado?: EstadoFactura;
    busqueda?: string;
    pageIndex?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<InvoiceSummary>> {
    let query = (this.supabase as any)
      .from('invoice_summaries_with_ref')
      .select(COLUMNAS_RESUMEN, { count: 'exact' })
      .eq('business_id', params.businessId)
      .order('fecha_emision', { ascending: false })
      .order('created_at', { ascending: false });

    if (params.branchId) {
      query = query.eq('branch_id', params.branchId);
    }

    // Filtro por estado
    if (params.estado) {
      query = query.eq('estado', params.estado);
    }

    // Búsqueda por receptor o número de comprobante
    if (params.busqueda) {
      query = query.or(
        `denominacion_receptor.ilike.%${params.busqueda}%,documento_receptor.ilike.%${params.busqueda}%`
      );
    }

    // Paginación
    if (params.pageIndex !== undefined && params.pageSize !== undefined) {
      const desde = params.pageIndex * params.pageSize;
      const hasta = desde + params.pageSize - 1;
      query = query.range(desde, hasta);
    } else {
      query = query.limit(LIMITE_LISTADO_DEFAULT);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('[InvoiceService] Error al obtener facturas paginadas:', error);
      return { data: [], total: 0 };
    }

    return {
      data: (data as InvoiceSummary[]) ?? [],
      total: count ?? 0,
    };
  }

  /**
   * Obtiene el detalle completo de un comprobante por su ID.
   * Incluye las relaciones de origen (venta/turno) para trazabilidad.
   *
   * @param invoiceId UUID del comprobante en `public.invoices`.
   * @returns Promesa con el comprobante completo o `null` si no se encuentra.
   */
  async obtenerFacturaPorId(invoiceId: string): Promise<Invoice | null> {
    const { data, error } = await (this.supabase as any)
      .from('invoices')
      .select(`
        *,
        relaciones:invoice_relations(
          id,
          sale_id,
          booking_id
        )
      `)
      .eq('id', invoiceId)
      .single();

    if (error) {
      console.error('[InvoiceService] Error al obtener factura por ID:', error);
      return null;
    }

    return data as Invoice ?? null;
  }

  /**
   * Contexto para emitir una Nota de Crédito sobre una factura: si anula la venta completa,
   * caja abierta, política de devolución y medios de pago de la venta con lo disponible.
   * @param invoiceId UUID de la factura a anular.
   */
  async obtenerContextoNotaCredito(invoiceId: string): Promise<CreditNoteContext> {
    const { data, error } = await (this.supabase as any).rpc('get_credit_note_context', { p_invoice_id: invoiceId });
    if (error) throw error;
    return data as CreditNoteContext;
  }

  /**
   * Lo que necesita el diálogo "Aplicar anulación": modo de devolución, cobros por medio, reparto
   * elegido al emitir y si hay caja abierta.
   * @param creditNoteId UUID de la Nota de Crédito con la anulación pendiente.
   */
  async obtenerContextoAplicarAnulacion(creditNoteId: string): Promise<CreditNoteApplyContext> {
    const { data, error } = await (this.supabase as any).rpc('get_credit_note_apply_context', { p_credit_note_id: creditNoteId });
    if (error) throw error;
    return data as CreditNoteApplyContext;
  }

  /**
   * Aplica la anulación de la venta de una NC aprobada que quedó pendiente (no había caja
   * abierta cuando ARCA la aprobó, o falló). Requiere caja abierta en la sucursal de la venta.
   * @param creditNoteId UUID de la Nota de Crédito.
   * @param refunds Reparto elegido al aplicar (modo sugerido). Sin él, se usa el elegido al emitir.
   */
  async aplicarAnulacionPorNotaCredito(creditNoteId: string, refunds?: SaleRefundLine[]): Promise<void> {
    const { error } = await (this.supabase as any).rpc('apply_credit_note_cancellation', {
      p_invoice_id: creditNoteId,
      p_refunds: refunds ?? null,
    });
    if (error) throw error;
  }

  /**
   * Obtiene la URL del PDF de un comprobante aprobado.
   * El PDF es generado por el Gateway AFIP y almacenado en S3/R2.
   * La URL es pre-firmada y tiene validez limitada en el tiempo.
   *
   * @param invoiceId UUID del comprobante.
   * @returns URL del PDF o `null` si el comprobante no fue aprobado aún.
   */
  async obtenerUrlPdf(invoiceId: string): Promise<string | null> {
    try {
      const { data, error } = await this.supabase.functions.invoke<{ pdfUrl: string }>(
        `invoice-pdf?invoiceId=${invoiceId}`,
        {
          method: 'GET'
        }
      );

      if (error) {
        console.error('[InvoiceService] Error devuelto por invoice-pdf:', error);
        throw new Error(error.message || 'No se pudo obtener el PDF del Gateway.');
      }

      if (!data || !data.pdfUrl) {
        return null;
      }

      return data.pdfUrl;
    } catch (err: any) {
      console.error('[InvoiceService] Error al obtener PDF:', err);
      throw err;
    }
  }

  // -------------------------------------------------------------------------
  // SECCIÓN 4: MOTOR DE SUSCRIPCIÓN EN TIEMPO REAL (Supabase Realtime)
  // -------------------------------------------------------------------------

  /**
   * Inicializa la escucha del canal PRIVADO `invoices:business:<id>`. La base (trigger
   * `trg_invoices_notificar_cambio`) manda un broadcast SOLO cuando cambia el estado de un
   * comprobante o el efecto sobre la venta; no hace falta comparar valores anteriores.
   *
   * Eventos:
   *   - `estado_changed`: actualiza el signal `cambioEstado` y muestra el cartel que corresponda
   *     (aprobada / rechazada / error fatal; PENDING no muestra nada).
   *   - `sale_effect_changed`: actualiza el signal `cambioVenta` (sin cartel).
   *
   * AISLAMIENTO MULTI-TENANT: el topic lleva el `business_id` y la policy de `realtime.messages`
   * solo deja recibir el de la empresa del usuario.
   *
   * Llamar una única vez por sesión (layout principal, tras autenticarse).
   *
   * @param businessId UUID del negocio del usuario autenticado.
   */
  inicializarEscuchaRealtime(businessId: string): void {
    if (this.canalRealtime) {
      return;
    }

    const canal = this.supabase
      .channel(`invoices:business:${businessId}`, { config: { private: true } })
      .on('broadcast', { event: 'estado_changed' }, ({ payload }) =>
        this._alCambiarEstado(payload as InvoiceCambioEstado)
      )
      .on('broadcast', { event: 'sale_effect_changed' }, ({ payload }) =>
        this._cambioVenta.set({ ...(payload as InvoiceCambioVenta) })
      );
    this.canalRealtime = canal;

    // Los canales privados necesitan el JWT del usuario antes de suscribirse.
    void this.supabase.realtime.setAuth().then(() => {
      canal.subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error(`[InvoiceService] Canal Realtime de facturas: ${status}`);
        }
      });
    });
  }

  /**
   * Detiene y limpia el canal de escucha Realtime.
   * Llamar al cerrar sesión o destruir el contexto de la aplicación.
   */
  detenerEscuchaRealtime(): void {
    if (this.canalRealtime) {
      this.supabase.removeChannel(this.canalRealtime);
      this.canalRealtime = null;
    }
  }

  /**
   * Resumen actual de un comprobante (misma forma que el listado). Sirve para refrescar UNA fila
   * cuando llega un aviso o como respaldo mientras el comprobante sigue pendiente.
   *
   * @param invoiceId UUID del comprobante.
   * @returns La fila de la vista o `null` si no existe / no es visible para el usuario.
   */
  async obtenerResumenPorId(invoiceId: string): Promise<InvoiceSummary | null> {
    const { data, error } = await (this.supabase as any)
      .from('invoice_summaries_with_ref')
      .select(COLUMNAS_RESUMEN)
      .eq('id', invoiceId)
      .maybeSingle();

    if (error) {
      console.error('[InvoiceService] Error al obtener el resumen del comprobante:', error);
      return null;
    }
    return (data as InvoiceSummary) ?? null;
  }

  // -------------------------------------------------------------------------
  // SECCIÓN 5: MÉTODOS PRIVADOS — Handlers de Realtime
  // -------------------------------------------------------------------------

  /**
   * Publica el cambio de estado y muestra el cartel correspondiente, solo si el comprobante
   * es de la sucursal de trabajo (o no hay una elegida).
   */
  private _alCambiarEstado(cambio: InvoiceCambioEstado): void {
    this._cambioEstado.set({ ...cambio });

    const aviso = avisoPorCambioEstado(cambio);
    if (!aviso || !avisoAplicaASucursal(cambio.branch_id, this.branches.workingBranchId())) {
      return;
    }
    if (aviso.tipo === 'success') {
      this.ui.showSuccess(aviso.texto, aviso.duracionMs);
    } else {
      this.ui.showError(aviso.texto, aviso.duracionMs);
    }
  }

  /**
   * Libera el flag de "emitiendo" tras la finalización de una llamada
   * a `emitirFactura`. Debe invocarse desde el componente al suscribirse
   * al Observable (en `next` y `error`).
   *
   * @example
   * this.invoiceService.emitirFactura(payload).subscribe({
   *   next: () => this.invoiceService.liberarEstadoEmision(),
   *   error: () => this.invoiceService.liberarEstadoEmision(),
   * });
   */
  liberarEstadoEmision(): void {
    this._emitiendo.set(false);
  }

  /**
   * Obtiene la lista de plantillas recurrentes del negocio activo.
   */
  async obtenerPlantillas(params: {
    businessId: string;
    search?: string;
    frecuencia?: string;
  }): Promise<InvoiceTemplate[]> {
    let query = (this.supabase as any)
      .from('invoice_templates')
      .select('*')
      .eq('business_id', params.businessId);

    if (params.search) {
      query = query.ilike('descripcion_item', `%${params.search}%`);
    }

    if (params.frecuencia) {
      query = query.eq('frecuencia', params.frecuencia);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('[InvoiceService] Error al obtener plantillas recurrentes:', error);
      return [];
    }

    return (data as InvoiceTemplate[]) ?? [];
  }

  /**
   * Alterna el estado activo de una plantilla recurrente.
   */
  async toggleEstadoPlantilla(templateId: string, isActive: boolean): Promise<boolean> {
    const { error } = await (this.supabase as any)
      .from('invoice_templates')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', templateId);

    if (error) {
      console.error('[InvoiceService] Error al cambiar estado de plantilla:', error);
      return false;
    }
    return true;
  }

  /**
   * Obtiene una plantilla recurrente por su ID.
   */
  async obtenerPlantillaPorId(templateId: string): Promise<InvoiceTemplate | null> {
    const { data, error } = await (this.supabase as any)
      .from('invoice_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (error) {
      console.error('[InvoiceService] Error al obtener plantilla por ID:', error);
      return null;
    }
    return data as InvoiceTemplate;
  }

  /**
   * Guarda una plantilla (crea si no tiene ID, actualiza si ya tiene ID).
   */
  async guardarPlantilla(template: Partial<InvoiceTemplate>): Promise<InvoiceTemplate | null> {
    if (template.id) {
      const { data, error } = await (this.supabase as any)
        .from('invoice_templates')
        .update({
          ...template,
          updated_at: new Date().toISOString()
        })
        .eq('id', template.id)
        .select()
        .single();

      if (error) {
        console.error('[InvoiceService] Error al actualizar plantilla:', error);
        return null;
      }
      return data as InvoiceTemplate;
    } else {
      const { data, error } = await (this.supabase as any)
        .from('invoice_templates')
        .insert([template])
        .select()
        .single();

      if (error) {
        console.error('[InvoiceService] Error al crear plantilla:', error);
        return null;
      }
      return data as InvoiceTemplate;
    }
  }
}
